export interface FieldAlias {
  key: string;
  aliases: string[];
}

const FIELD_ALIASES: Record<string, string[]> = {
  name: ["name", "nombre", "finca", "organization", "org", "farmname", "farm", "nombredelafinca", "nombredelafinca"],
  idTypeId: ["idtype", "tipoid", "tipodoc", "tipodocumento", "documenttype"],
  idNumber: ["idnumber", "codigo", "codigodefinca", "codigofinca", "numerodoc", "nit", "cc", "cedula", "identificacion"],
  logo: ["logo", "logourl", "imagen"],
  isActive: ["isactive", "activo", "active", "estado"],
  isFarm: ["isfarm", "esfinca", "finca", "tipo_finca"],
  ownerName: ["ownername", "owner", "propietario", "responsable", "dueno", "dueño", "farmer", "primernombre", "primernombredelpropietario"],
  ownerIdTypeId: ["owneridtype", "tipoidpropietario", "tipodocpropietario", "owneridtypeid", "tipodocumentodelpropietario"],
  ownerIdNumber: ["owneridnumber", "propietarioid", "documentopropietario", "ccpropietario", "cedulapropietario", "documento", "cedula"],
  ownerLastName: ["ownerlastname", "apellido", "apellidos", "primerapellido", "apellidodelpropietario"],
  ownerMiddleName: ["ownermiddlename", "segundonombre", "middle", "segundonombredelpropietario"],
  ownerGender: ["ownergender", "genero", "sexo", "generodelpropietario"],
  ownerBirthdate: ["ownerbirthdate", "fechanacimiento", "nacimiento", "cumpleanos", "cumpleaños", "fechadenacimientodelpropietario"],
  ownerEmail: ["owneremail", "email", "correo", "correoelectronico", "correodelpropietario"],
  ownerAddress: ["owneraddress", "direccion", "direcci", "direcciondelpropietario"],
  ownerNickName: ["ownernickname", "alias", "apodo", "nickname"],
  ownerPhoneNumbers: ["phone", "telefono", "tel", "celular", "movil", "móvil", "whatsapp", "contacto", "numero", "número"],
  cityId: ["city", "ciudad", "municipio", "cityid"],
  regionId: ["region", "departamento", "estado", "provincia", "regionid"],
  countryId: ["country", "pais", "país", "countryid"],
  corregimientoId: ["corregimiento", "corregimientoid"],
  veredaId: ["vereda", "veredaid", "caserio"],
  latitude: ["lat", "latitude", "latitud"],
  longitude: ["long", "lng", "longitude", "longitud"],
  elevation: ["elevation", "altitud", "altura", "elevacion", "elevación", "elevationm"],
  geometry: ["geometry", "geojson", "poligono", "geometria", "geometría"],
  area: ["area", "superficie", "hectareas", "hectáreas", "extension", "extensión", "tamano", "tamaño"],
  malesFullTimeWorkers: ["malesfulltime", "hombrestiempocompleto", "hombresft", "malesft"],
  malesTemporalWorkers: ["malestemporal", "hombrestemporales", "hombrestemp", "malestemp"],
  femaleFullTimeWorkers: ["femalesfulltime", "mujerestiempocompleto", "mujeresft", "femalesft"],
  femaleTemporalWorkers: ["femalestemporal", "mujerestemporales", "mujerestemp", "femalestemp"],
  groupIds: ["group", "grupo", "groupid", "grupoid", "groupids"],
};

const COORDINATE_COMBINED_ALIASES = [
  "lat_long", "latlong", "lat-lng", "latlng", "coordinates", "coords",
  "gps", "location", "ubicacion", "ubicación", "punto", "geo",
];

const PHONE_PREFIXES = ["phone", "telefono", "tel", "celular", "movil", "móvil", "whatsapp", "contacto", "numero", "número"];
const GROUP_PREFIXES = ["group", "grupo"];

export type MatchType = "exact" | "fuzzy" | "alias" | "none";

export interface SmartMatchResult {
  fieldKey: string;
  csvColumn: string;
  matchType: MatchType;
  confidence: number;
}

export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_\-./\\]+/g, "");
}

function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = Array(a.length + 1)
    .fill(null)
    .map(() => Array(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
}

function similarityScore(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshteinDistance(a, b);
  return 1 - dist / maxLen;
}

export interface SmartMatchOptions {
  multiColumnFields?: string[];
  coordinateCombinedFields?: { columns: string[]; aliases: string[] };
}

export interface MultiColumnMatch {
  fieldKey: string;
  csvColumns: string[];
  matchType: MatchType;
  confidence: number;
}

export interface CoordinateMatch {
  csvColumn: string;
  matchType: MatchType;
  confidence: number;
}

export interface FullSmartMatchResult {
  singleMatches: SmartMatchResult[];
  multiColumnMatches: MultiColumnMatch[];
  coordinateMatch: CoordinateMatch | null;
}

const OWNER_FIELD_KEYS = new Set([
  "ownerName",
  "ownerIdTypeId",
  "ownerIdNumber",
  "ownerLastName",
  "ownerMiddleName",
  "ownerGender",
  "ownerBirthdate",
  "ownerEmail",
  "ownerAddress",
  "ownerNickName",
]);

const AMBIGUOUS_TERMS = new Set([
  "nombre", "name", "cedula", "id", "numero",
  "direccion", "email", "correo", "telefono",
  "genero", "sexo",
]);

const OWNER_CONTEXT_TERMS = [
  "owner", "propietario", "dueno", "dueño", "responsable", "farmer", "user",
];

function hasOwnerContext(normalizedHeader: string): boolean {
  return OWNER_CONTEXT_TERMS.some((term) => normalizedHeader.includes(term));
}

const FIELD_PRIORITY: string[] = [
  "name",
  "idTypeId",
  "idNumber",
  "ownerName",
  "ownerLastName",
  "ownerMiddleName",
  "ownerNickName",
  "ownerIdTypeId",
  "ownerIdNumber",
  "ownerGender",
  "ownerBirthdate",
  "ownerEmail",
  "ownerAddress",
  "ownerPhoneNumbers",
  "cityId",
  "regionId",
  "countryId",
  "corregimientoId",
  "veredaId",
  "latitude",
  "longitude",
  "elevation",
  "geometry",
  "area",
  "malesFullTimeWorkers",
  "malesTemporalWorkers",
  "femaleFullTimeWorkers",
  "femaleTemporalWorkers",
  "crops",
  "groupIds",
  "logo",
  "isActive",
  "isFarm",
];

export function smartMatch(
  csvHeaders: string[],
  fieldKeys: string[],
  options?: { multiColumnFields?: string[] }
): FullSmartMatchResult {
  const normalizedHeaders = csvHeaders.map((h) => ({
    original: h,
    normalized: normalizeString(h),
  }));

  const usedHeaders = new Set<string>();
  const singleMatches: SmartMatchResult[] = [];
  const multiColumnFields = options?.multiColumnFields ?? ["ownerPhoneNumbers", "groupIds"];

  // Phase 1: Coordinate combined column detection
  let coordinateMatch: CoordinateMatch | null = null;
  for (const header of normalizedHeaders) {
    const isCoord = COORDINATE_COMBINED_ALIASES.some(
      (alias) => header.normalized === alias || similarityScore(header.normalized, alias) > 0.8
    );
    if (isCoord) {
      coordinateMatch = {
        csvColumn: header.original,
        matchType: "alias",
        confidence: 0.95,
      };
      usedHeaders.add(header.original);
      break;
    }
  }

  // Phase 2: Multi-column field detection (phones, groups)
  const multiColumnMatches: MultiColumnMatch[] = [];
  for (const fieldKey of multiColumnFields) {
    const prefixes = fieldKey === "ownerPhoneNumbers" ? PHONE_PREFIXES : GROUP_PREFIXES;
    const matchedColumns: { column: string; confidence: number }[] = [];

    for (const header of normalizedHeaders) {
      if (usedHeaders.has(header.original)) continue;
      const isMatch = prefixes.some(
        (prefix) =>
          header.normalized.startsWith(prefix) ||
          header.normalized === prefix ||
          header.normalized.includes(prefix)
      );
      if (isMatch) {
        matchedColumns.push({ column: header.original, confidence: 0.85 });
      }
    }

    if (matchedColumns.length > 0) {
      matchedColumns.forEach((m) => usedHeaders.add(m.column));
      multiColumnMatches.push({
        fieldKey,
        csvColumns: matchedColumns.map((m) => m.column),
        matchType: "alias",
        confidence: 0.85,
      });
    }
  }

  // Phase 3: Single-field matching (priority-ordered)
  const orderedFieldKeys = [...fieldKeys].sort((a, b) => {
    const ai = FIELD_PRIORITY.indexOf(a);
    const bi = FIELD_PRIORITY.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  for (const fieldKey of orderedFieldKeys) {
    if (multiColumnMatches.some((m) => m.fieldKey === fieldKey)) continue;

    let bestMatch: SmartMatchResult | null = null;
    let bestScore = 0;
    const aliases = FIELD_ALIASES[fieldKey] || [];
    const normalizedFieldKey = normalizeString(fieldKey);
    const isOwnerField = OWNER_FIELD_KEYS.has(fieldKey);

    for (const header of normalizedHeaders) {
      if (usedHeaders.has(header.original)) continue;

      let score = 0;
      let matchType: MatchType = "none";

      if (header.normalized === normalizedFieldKey) {
        score = 1.0;
        matchType = "exact";
      } else if (aliases.includes(header.normalized)) {
        score = 0.95;
        matchType = "alias";
      } else if (aliases.some((a) => similarityScore(header.normalized, a) > 0.85)) {
        score = 0.88;
        matchType = "alias";
      } else if (header.normalized.includes(normalizedFieldKey) || normalizedFieldKey.includes(header.normalized)) {
        score = 0.75;
        matchType = "fuzzy";
      } else {
        const sim = similarityScore(header.normalized, normalizedFieldKey);
        if (sim > 0.7) {
          score = sim;
          matchType = "fuzzy";
        }
        for (const alias of aliases) {
          const aliasSim = similarityScore(header.normalized, alias);
          if (aliasSim > score && aliasSim > 0.7) {
            score = aliasSim;
            matchType = "fuzzy";
          }
        }
      }

      // Owner disambiguation: if this is an owner field and the header has no
      // owner context, penalize the score for ambiguous terms so org fields win.
      if (isOwnerField && score > 0 && !hasOwnerContext(header.normalized)) {
        const headerMatchesAmbiguous = aliases.some(
          (a) => AMBIGUOUS_TERMS.has(a) && (header.normalized === a || similarityScore(header.normalized, a) > 0.85)
        );
        if (headerMatchesAmbiguous) {
          score *= 0.5;
        }
      }

      // Non-owner field gets a boost when the header explicitly mentions owner context,
      // so it does NOT accidentally grab owner-specific columns.
      if (!isOwnerField && hasOwnerContext(header.normalized)) {
        score *= 0.3;
      }

      if (score > bestScore && score >= 0.7) {
        bestScore = score;
        bestMatch = {
          fieldKey,
          csvColumn: header.original,
          matchType,
          confidence: score,
        };
      }
    }

    if (bestMatch) {
      usedHeaders.add(bestMatch.csvColumn);
      singleMatches.push(bestMatch);
    }
  }

  return { singleMatches, multiColumnMatches, coordinateMatch };
}

export { FIELD_ALIASES, COORDINATE_COMBINED_ALIASES };
